import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  output,
  signal,
} from '@angular/core';
import {
  parseColumnMultiFilter,
  SortState,
} from '../data-table/data-table.types';
import { UiDataTableComponent } from '../data-table/data-table.component';
import { UiEntityActionLogFilterOption, UiEntityActionLogRow } from './entity-action-log.types';

const DEFAULT_ACCION_OPTIONS: readonly UiEntityActionLogFilterOption[] = [
  { id: 'nuevo', label: 'Nuevo', value: 'Nuevo' },
  { id: 'editar', label: 'Editar', value: 'Editar' },
  { id: 'activar', label: 'Activar', value: 'Activar' },
  { id: 'inactivar', label: 'Inactivar', value: 'Inactivar' },
  { id: 'eliminar', label: 'Eliminar', value: 'Eliminar' },
];

const DEFAULT_DISPOSITIVO_OPTIONS: readonly UiEntityActionLogFilterOption[] = [
  { id: 'computadora', label: 'Computadora', value: 'Computadora' },
  { id: 'tableta', label: 'Tableta', value: 'Tableta' },
  { id: 'celular', label: 'Celular', value: 'Celular' },
];

const DEFAULT_NAVEGADOR_OPTIONS: readonly UiEntityActionLogFilterOption[] = [
  { id: 'chrome', label: 'Google Chrome', value: 'Google Chrome' },
  { id: 'safari', label: 'Safari', value: 'Safari' },
  { id: 'edge', label: 'Edge', value: 'Edge' },
];

/**
 * Organismo: bitácora de acciones de una entidad (tabla de solo lectura).
 * Presentacional: no conoce dominio ni API; recibe filas y emite eventos.
 */
@Component({
  selector: 'ui-entity-action-log',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [UiDataTableComponent],
  templateUrl: './entity-action-log.component.html',
  styleUrl: './entity-action-log.component.scss',
  host: { class: 'ui-entity-action-log-host' },
})
export class UiEntityActionLogComponent {
  readonly rows = input<readonly UiEntityActionLogRow[]>([]);
  readonly loading = input(false);
  readonly title = input('Bitácora de acciones');
  /** Si no se envían, la columna conserva las opciones de la maqueta. */
  readonly accionOptions = input<readonly UiEntityActionLogFilterOption[] | null>(null);
  /** Si se envían, Usuario pasa a filtro de selección múltiple. */
  readonly usuarioOptions = input<readonly UiEntityActionLogFilterOption[] | null>(null);
  readonly dispositivoOptions = input<readonly UiEntityActionLogFilterOption[] | null>(null);
  readonly navegadorOptions = input<readonly UiEntityActionLogFilterOption[] | null>(null);

  readonly exportExcel = output<void>();

  private readonly sourceRows = signal<UiEntityActionLogRow[]>([]);
  protected readonly displayRows = signal<UiEntityActionLogRow[]>([]);
  protected readonly total = computed(() => this.displayRows().length);

  private activeColumnFilters: Record<string, string> = {};

  protected readonly columns = computed(() => {
    const usuarioOptions = this.usuarioOptions();
    return [
      { key: 'fechaHora', header: 'Fecha y hora', type: 'string' as const },
      {
        key: 'accion',
        header: 'Acción',
        type: 'string' as const,
        filterType: 'multiselect' as const,
        filterOptions: [...(this.accionOptions() ?? DEFAULT_ACCION_OPTIONS)],
      },
      {
        key: 'usuario',
        header: 'Usuario',
        type: 'string' as const,
        ...(usuarioOptions
          ? {
              filterType: 'multiselect' as const,
              filterOptions: [...usuarioOptions],
            }
          : {}),
      },
      {
        key: 'dispositivo',
        header: 'Dispositivo',
        type: 'string' as const,
        filterType: 'multiselect' as const,
        filterOptions: [...(this.dispositivoOptions() ?? DEFAULT_DISPOSITIVO_OPTIONS)],
      },
      {
        key: 'navegador',
        header: 'Navegador',
        type: 'string' as const,
        filterType: 'multiselect' as const,
        filterOptions: [...(this.navegadorOptions() ?? DEFAULT_NAVEGADOR_OPTIONS)],
      },
      { key: 'ip', header: 'IP', type: 'string' as const },
      { key: 'ubicacion', header: 'Ubicación', type: 'string' as const },
    ];
  });

  constructor() {
    effect(() => {
      const next = [...this.rows()];
      this.sourceRows.set(next);
      this.applyFilters();
    });
  }

  protected onExportExcel(): void {
    this.exportExcel.emit();
  }

  protected onSort(event: SortState): void {
    this.displayRows.update(rows =>
      [...rows].sort((a, b) => {
        const va = a[event.sortBy as keyof UiEntityActionLogRow] ?? '';
        const vb = b[event.sortBy as keyof UiEntityActionLogRow] ?? '';
        return event.order === 'asc'
          ? String(va).localeCompare(String(vb), 'es', { numeric: true })
          : String(vb).localeCompare(String(va), 'es', { numeric: true });
      }),
    );
  }

  protected onFilter(filters: Record<string, string>): void {
    this.activeColumnFilters = filters;
    this.applyFilters();
  }

  private applyFilters(): void {
    const base = this.sourceRows();
    const result = base.filter(row =>
      Object.entries(this.activeColumnFilters).every(([key, val]) => {
        if (!val) return true;
        if (this.isMultiFilter(key)) {
          const selected = parseColumnMultiFilter(val);
          return (
            !selected.length ||
            selected.includes(String(row[key as keyof UiEntityActionLogRow] ?? ''))
          );
        }
        return String(row[key as keyof UiEntityActionLogRow] ?? '')
          .toLowerCase()
          .includes(val.toLowerCase());
      }),
    );
    this.displayRows.set(result);
  }

  private isMultiFilter(key: string): boolean {
    if (key === 'usuario') return this.usuarioOptions() != null;
    return key === 'accion' || key === 'dispositivo' || key === 'navegador';
  }
}
